import React, { useEffect, useState } from 'react';
import { useZodFormFieldSingle } from '~/components/zod/ZodForm';
import LegacyResponsiveImage from '~/components/LegacyResponsiveImage';
import ZodFieldCheckbox from '~/components/zod/ZodFieldCheckbox';
import { Money } from '~/components/Money';
import DemoPhotographyConfig from '~/tenants/demo/performable/photos/DemoPhotographyConfig';
import ZodFieldHidden from '~/components/zod/ZodFieldHidden';
import { PackageFormNested, usePackageFormContext } from '~/components/performable/PackageForm';
import { DemoOrderContext } from '~/tenants/demo/model/DemoOrder';
import { DEMO_PHOTOGRAPHY_OPTIONS } from '~/tenants/demo/performable/photos/DemoPhotographyJob';
import { DEMO_FLOORPLANS_OPTIONS } from '~/tenants/demo/performable/floorplans/DemoFloorplansJob';
import DemoFloorplansConfig from '~/tenants/demo/performable/floorplans/DemoFloorplansConfig';
import { TenantPackageInclude } from '~/tenants/common/package';
import Demo3dConfig from '~/tenants/demo/performable/3d/Demo3dConfig';
import { DEMO_3D_OPTIONS } from '~/tenants/demo/performable/3d/Demo3dJob';
import { DEMO_AERIAL_OPTIONS } from '~/tenants/demo/performable/aerial/DemoAerialJob';
import DemoAerialConfig from '~/tenants/demo/performable/aerial/DemoAerialConfig';
import DemoPhotosTwilight from '~/tenants/demo/performable/photos/DemoPhotosTwilight';
import {
  DemoPhotosCommunityCustom,
  DemoPhotosCommunityLicense,
} from '~/tenants/demo/performable/photos/DemoPhotosCommunity';

export function useDemoPackagePhotoOption() {
  const { orderContext } = usePackageFormContext<DemoOrderContext>();

  const sqft = 'sqft' in orderContext.metadata ? orderContext.metadata.sqft : 0;

  const photos = Object.entries(DEMO_PHOTOGRAPHY_OPTIONS).find(
    ([_id, pkg]) => sqft <= pkg.to && sqft >= pkg.from,
  );

  if (!photos) {
    throw new Error('No photography package found for sqft ' + sqft);
  }

  return { id: photos[0], ...photos[1] };
}

export function useDemoPackage3dOption() {
  const { orderContext, package: pkg } = usePackageFormContext<DemoOrderContext>();
  const sqft = 'sqft' in orderContext.metadata ? orderContext.metadata.sqft : 0;

  const scan = Object.entries(DEMO_3D_OPTIONS).find(
    ([_id, pkg]) => sqft <= pkg.to && sqft >= pkg.from,
  );

  if (!scan) {
    throw new Error('No 3d package found for sqft ' + sqft);
  }

  return scan[0];
}

export function useDemoPackageAerialOption() {
  const { orderContext } = usePackageFormContext<DemoOrderContext>();

  const sqft = 'sqft' in orderContext.metadata ? orderContext.metadata.sqft : 0;
  const acres = 'acres' in orderContext.metadata ? orderContext.metadata.acres : 0;

  const aerial = Object.entries(DEMO_AERIAL_OPTIONS).find(([_id, pkg]) => acres <= pkg.acres);

  if (!aerial) {
    throw new Error('No aerial package found for sqft ' + sqft);
  }

  return aerial[0];
}

export function useDemoPackageFloorplanOption() {
  const { orderContext } = usePackageFormContext<DemoOrderContext>();

  const sqft = 'sqft' in orderContext.metadata ? orderContext.metadata.sqft : 0;

  const floorplans = Object.entries(DEMO_FLOORPLANS_OPTIONS).find(
    ([_id, pkg]) => sqft <= pkg.to && sqft >= pkg.from,
  );

  if (!floorplans) {
    throw new Error('No floorplans package found for sqft ' + sqft);
  }

  return floorplans[0];
}

function DemoPackageCommunityHidden(props: { enable: boolean }) {
  const [_name, value, setValue] = useZodFormFieldSingle('community');

  useEffect(() => {
    if (props.enable.toString() !== value) {
      setValue(props.enable.toString());
    }
  }, [props.enable]);

  return <ZodFieldHidden name="community" value={value} />;
}

export function DemoPackageCommunityAddOn(props: { aerial?: boolean }) {
  const [enabled, setEnabled] = useState(false);

  return (
    <div className="flex max-sm:flex-col items-center sm:items-start max-sm:space-y-4 sm:space-x-6">
      <div className="rounded-lg overflow-hidden shrink-0">
        <LegacyResponsiveImage src="card/public/community.jpg" width={200} />
      </div>
      <div className="space-y-4">
        <PackageFormNested performable={DemoPhotographyConfig}>
          <DemoPhotosCommunityCustom />
          <DemoPhotosCommunityLicense />
        </PackageFormNested>
        {props.aerial && (
          <PackageFormNested performable={DemoAerialConfig}>
            <div>
              <div className="flex items-center leading-none select-none">
                <div className="w-8 shrink-0">
                  <ZodFieldCheckbox id="aerial" name="community" />
                </div>
                <label className="text-base font-medium cursor-pointer w-full" htmlFor="aerial">
                  Aerial Community Photos
                </label>
                <p className="font-bold text-base">
                  <Money>60</Money>
                </p>
              </div>
              <div className="pl-8 pt-2 max-sm:pr-4">
                <p className="text-sm text-gray-600">
                  A high resolution example of directed shots that showcase your desired, local sites.
                </p>
              </div>
            </div>
          </PackageFormNested>
        )}
      </div>
    </div>
  );
}

export function DemoPackagePhotoInclude() {
  const { photos } = useDemoPackagePhotoOption();

  return (
    <TenantPackageInclude name={`${photos} Listing Photos`} image="card/public/photos.jpg">
      {DemoPhotographyConfig.short}
    </TenantPackageInclude>
  );
}

export function DemoPackageFloorplanInclude() {
  return (
    <TenantPackageInclude name={DemoFloorplansConfig.name} image="card/public/floorplan.jpg">
      {DemoFloorplansConfig.short}
    </TenantPackageInclude>
  );
}

export function DemoPackageAerialInclude() {
  return (
    <TenantPackageInclude name={DemoAerialConfig.name} image="card/public/aerial.jpg">
      {DemoAerialConfig.short}
    </TenantPackageInclude>
  );
}

export function DemoPackage3dInclude() {
  return (
    <TenantPackageInclude name="3D Scan" image="card/public/matterport.jpg">
      {Demo3dConfig.short}
    </TenantPackageInclude>
  );
}

export function DemoPackagePhotoAddOns() {
  const { id } = useDemoPackagePhotoOption();

  return (
    <PackageFormNested performable={DemoPhotographyConfig}>
      <DemoPhotosTwilight />
      <ZodFieldHidden name="option" value={id} />
    </PackageFormNested>
  );
}

export function DemoPackage3dAddOns() {
  const option = useDemoPackage3dOption();

  return (
    <PackageFormNested performable={Demo3dConfig}>
      <ZodFieldHidden name="option" value={option} />
    </PackageFormNested>
  );
}

export function DemoPackageAerialAddOns() {
  const option = useDemoPackageAerialOption();

  return (
    <PackageFormNested performable={DemoAerialConfig}>
      <ZodFieldHidden name="option" value={option} />
    </PackageFormNested>
  );
}

export function DemoPackageFloorplanAddOns() {
  const option = useDemoPackageFloorplanOption();

  return (
    <PackageFormNested performable={DemoFloorplansConfig}>
      <ZodFieldHidden name="option" value={option} />
    </PackageFormNested>
  );
}
